import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePostGrnOrderMutation } from "../../../../store/api/grnapi";
import ActionButtonDropdown from "../../../../Components/ActionButtonDropdown";
import Pagination from "../../../../Core/Pagination/Pagination";
import ListLayout from "../../../../Core/Layout/ListLayout";
import Table from "../../../../Core/Table/Table";
import Tabs from "../../../../Components/Tabs";
import ConfigureColumnsNew from "../../../../Core/ConfigureColumnsNew/ConfigureColumnsNew";
import ExportCSV from '../../../../Core/Export/ExportCSV';
import ListFilters from "../../../../Core/ListFilters/ListFilters";

import { useDeleteDocMutation } from "../../../../store/api/globalapi";
import DelConfirmPopup from "../../../../Components/Popup/DelConfirmPopup ";
import useScreenHeight from "../../../../utils/useScreenHeight";

const MainContent = () => {
  const navigate = useNavigate();
  const { screenHeight, screenScaling,pageSize } = useScreenHeight();

  const [currentPage, setCurrentPage] = useState(1);
  const [grnData, setGrnData] = useState({
    purchase_receipt: [],
    total_purchase_receipt: 0
  });
  const [loading, setLoading] = useState(true);
  const [grnDataApi] = usePostGrnOrderMutation();
  const [filters, setFilters] = useState({});
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteDoc] = useDeleteDocMutation();
  const [delPopupVisible, setDelPopupVisible] = useState(false);
  const [delSelectedId, setDelSelectedId] = useState(null);
  const [delmessage, setDelmessage] = useState();
  const [alreadySubmitData, setAlreadySubmitData] = useState(false)
  const [delDone, setDelDone] = useState(false)

  const filtersConfig = [
    { name: 'siNo', label: 'S. No.', width: '6%', type: 'text' },
    { name: 'grn_name', label: 'GRN Name', width: '12.7%', type: 'text' },
    { name: 'po_no', label: 'Purchase Order', width: '12.7%', type: 'text' },
    { name: 'no_line', label: 'No. of Lines', width: '10.7%', type: 'number' },
    { name: 'total_qty', label: 'Total Qty', width: '10%', type: 'number' },
    { name: 'supplier', label: 'Supplier', width: '15.7%', type: 'text' },
    {
      name: 'status',
      label: 'Status',
      width: '15.7%',
      type: 'select',
      options: [
        { value: 'To Bill', label: 'To Bill' },
        { value: 'Return Issued', label: 'Return Issued' },
        { value: 'Cancelled', label: 'Cancelled' },
        { value: 'Draft', label: 'Draft' },
        { value: 'Completed', label: 'Completed' }
      ]
    },
    {
      name: 'date_range',
      label: 'Date Range',
      type: 'date-range',
      width: '16%',
    },
  ];

  const [columnsConfig, setColumnsConfig] = useState(() => [
    {
      header: "S. No.",
      name: "S. No.",
      accessor: "siNo",
      width: '7%',
      cellRenderer: (row) => row.siNo,
      sortable: false,
      visible: true
    },
    {
      header: "GRN Id",
      name: "GRN Id",
      accessor: "name",
      sortable: true,
      width: '15.7%',
      visible: true
    },
    {
      header: "Purchase Order",
      name: "Purchase Order",
      accessor: "po_no",
      sortable: true,
      width: '15.7%',
      visible: true
    },
    {
      header: "No. of Lines",
      name: "No. of Lines",
      accessor: "no_of_lines",
      sortable: true,
      visible: true,
      sortFn: (a, b) => parseInt(a.no_of_lines) - parseInt(b.no_of_lines)
    },
    {
      header: "Total Quantity",
      name: "Total Quantity",
      accessor: "total_received_qty",
      width: '13.7%',
      sortable: true,
      visible: true,
      sortFn: (a, b) => parseInt(a.total_received_qty) - parseInt(b.total_received_qty)
    },
    {
      header: "Vendor",
      name: "Vendor",
      accessor: "vendor",
      sortable: true,
      visible: true
    },
    {
      header: "Status",
      name: "Status",
      accessor: "status",
      sortable: true,
      visible: true,
      cellRenderer: (row) => {
        const status = row.status;
        let color;

        switch (status) {
          case 'Draft':
            color = '#6366F1';
            break;
          case 'To Bill':
            color = '#F97316';
            break;
          case 'Completed':
            color = '#65A30D';
            break;
          case 'Cancelled':
            color = '#EF4444';
            break;
          case 'Return Issued':
            color = '#6B21A8';
            break;
          default:
            color = '#000000';
        }

        return (
          <span
            style={{
              backgroundColor: `${color}1A`,
              color,
              padding: '4px 10px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: 500,
              display: 'inline-block',
              textTransform: 'capitalize',
            }}
          >
            {status}
          </span>
        );
      }
    },
    {
      header: "Date",
      name: "Date",
      accessor: "posting_date",
      sortable: true,
      visible: true,
      sortFn: (a, b) => new Date(a.posting_date) - new Date(b.posting_date)
    },
    {
      header: "Action",
      name: "Action",
      width: '10%',
      sortable: false,
      accessor: "action",
      visible: true,
      cellRenderer: (row) => (
        <ActionButtonDropdown
          id={row.name}
          status={row.status}
          onView={() => handleView(row.name, row.status)}
          onEdit={() => handleEdit(row.name, row.status)}
          onDelete={() => handleDelete(row.name)}
        />
      )
    },
  ]);

  // Handle column configuration changes
  const handleSaveColumns = (selectedKeys, allFields) => {
    const updatedColumns = columnsConfig.map(column => {
      return {
        ...column,
        visible: selectedKeys.includes(column.accessor)
      };
    });
    setColumnsConfig(updatedColumns);
  };

  const visibleColumns = useMemo(() =>
    columnsConfig.filter(col => col.visible === true),
    [columnsConfig]
  );

  const defaultSelectedColumns = useMemo(() =>
    columnsConfig
      .filter(col => col.visible === true)
      .map(col => ({
        fieldname: col.accessor,
        label: col.name
      }))
      .filter(col => col.fieldname),
    [columnsConfig]
  );

  const handleView = (id, status) => {
    try {
      navigate(`/inbound/grn/view-purchase-receipt`, { state: { PurchaseId: id, status } });
    } catch (err) {
      console.error("View failed:", err);
    }
  };

  const handleEdit = async (id, status) => {
    try {
      navigate(`/inbound/grn/edit-purchase-receipt/`, { state: { PurchaseId: id, status } });
    } catch (err) {
      console.error("Edit failed:", err);
    }
  };

    const handleDelete = (id) => {
        setDelSelectedId(id);
        setDelPopupVisible(true)
        setAlreadySubmitData(false)
        setDelmessage("Are you sure you want to Delete this Purchase Receipt?");
    };

    const delHandleCancel = () => {
        setDelPopupVisible(false);
        setDelSelectedId(null);
    }

    const delHandleConfirm = async () => {
        
        try {
            await deleteDoc({ doctype: "Purchase Receipt", name: delSelectedId }).unwrap()
            setDelmessage(`Purchase Receipt ${delSelectedId} deleted successfully!`)
            setDelDone(true)
            setTimeout(() => {
                setDelPopupVisible(false);
                setDelSelectedId(null);
                setDelDone(false)
                getData();
            }, 1000);
        } catch (err) {
            console.error("Deletion Failed:", err)

            let errorMessage = "Failed to delete the Purchase Receipt!";

            //If the server is sending the error message , we will extract it.
            if (err?.data?._server_messages) {
                try {
                    const messageArr = JSON.parse(err.data._server_messages)
                    if (messageArr && messageArr.length > 0) {
                        //parse inner json object
                        const messageObj = JSON.parse(messageArr[0])
                        if (messageObj?.message) {
                            //extract the exact message we want
                            const fullMessage = messageObj.message
                            errorMessage = fullMessage.split('You must')[0].trim();
                            //remove html in the message
                            errorMessage = errorMessage.replace(/<[^>]+>/g, '');

                        }
                    }
                } catch (parseError) {
                    console.error("Error parsing server messages:", parseError);
                }
            }
            setDelPopupVisible(true)
            setDelmessage(errorMessage)
            setAlreadySubmitData(true);
        } finally {
            // setTimeout(() => {
            //     setDelPopupVisible(false);
            //     setDelSelectedId(null);
            // }, 2000);
        }
    };
  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(val => val !== null && val !== undefined && val !== '').length;
  }, [filters]);
  const actionButtons = [
    {
      className: "createbtn border-0 d-flex",
      style: { gap: '2px', height: '28px' },
      iconName: "IoFilterSharp",
      iconSize: 12,
      children: activeFilterCount > 0 ? `Filter (${activeFilterCount})` : "Filter",
      onClick: () => {
        setShowFilters(prev => !prev);
        setShowColumnConfig(false);
      }
    },
    {
      className: "btnsecond border-0 d-flex",
      style: { gap: '2px', height: '28px' },
      iconName: "MdOutlineRemoveRedEye",
      iconSize: 12,
      children: "Show more",
      onClick: () => {
        setShowColumnConfig(prev => !prev);
        setShowFilters(false);
      }
    },
    {
      className: "btnthird border-0 d-flex",
      style: { gap: '2px', height: '28px' },
      iconName: "RiFileExcel2Fill",
      iconSize: 12,
      children: "Export Excel",
      onClick: () => setShowExportPopup(true),
    },
    {
      className: "custom-btn-00989 d-flex",
      style: { gap: '2px', height: '28px' },
      iconName: "FiPlus",
      iconSize: 12,
      children: "Create GRN",
      to: "/inbound/grn/create-purchase-receipt",
    }
  ];

  const getData = async () => {
    setLoading(true);
    try {
      const filteredSearchInput = Object.entries(filters).reduce(
			(acc, [key, value]) => {
				if (value !== "" && value !== undefined && value !== null) {
					acc[key] = value;
				}
				return acc;
			},
			{}
		);
		const siNoFilter = filteredSearchInput.siNo;
		const isSiNoFilterActive = siNoFilter !== undefined && siNoFilter !== "";
		// STEP 3: If filtering by siNo, jump to that page
		const targetPage = isSiNoFilterActive
			? Math.ceil(parseInt(siNoFilter, 10) / pageSize)
			: currentPage;
      const response = await grnDataApi({
        start: pageSize * (targetPage - 1),
        page_length: pageSize,
        search_input: {
          grn_name: filters?.grn_name || "",
          po_no: filters?.po_no || "",
          min_no_of_lines: filters?.no_line || "",
          min_qty: filters?.total_qty || "",
          max_qty: filters?.total_qty || "",
          supplier: filters?.supplier || "",
          date_range: filters?.date_range || "",
          status: filters?.status || "",

        },
      });
      const rawData = response.data.data.purchase_receipt;
			// STEP 4: Assign global serial numbers
			const numberedData = rawData.map((item, index) => ({
				...item,
				siNo: index + 1 + pageSize * (targetPage - 1),
			}));

			// STEP 5: If siNo is used, filter client-side
			const finalData = isSiNoFilterActive
				? numberedData.filter((item) => item.siNo === parseInt(siNoFilter, 10))
				: numberedData;
        setGrnData({
					purchase_receipt: finalData,
					total_no_of_records: response.data.data.total_no_of_records,
				});
			// Optional: update currentPage to reflect jump
			if (isSiNoFilterActive) setCurrentPage(targetPage);
      // setGrnData(response.data.data);
    } catch (error) {
      console.error("Error fetching GRN data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setCurrentPage(1);
    setFilters(newFilters);
  };

  useEffect(() => {
    
    if ( pageSize > 0) {
        getData();
    }

  }, [currentPage, filters, pageSize, screenHeight,screenScaling]);
  const accessors = [
    "name",
    // "po_no",
    // "no_of_lines"
    "total_qty",
    "supplier",
    "status",
      "posting_date"
  ]
  return (
		<div
			className="container-fluid d-flex flex-column"
			style={{ flexGrow: "1" }}
		>
			<Tabs />
			<ListLayout
				breadcrumbItems={[
					{ label: "Inbound" },
					{ label: "GRN List", style: { color: "#4b002b" } },
				]}
				actionButtons={actionButtons}
			>
				{showFilters && (
					<ListFilters
						filtersConfig={filtersConfig}
						onFilterChange={handleFilterChange}
						currentFilters={filters}
						setShowFilters={setShowFilters}
					/>
				)}

				{showColumnConfig && (
					<ConfigureColumnsNew
						show={showColumnConfig}
						onClose={() => setShowColumnConfig(false)}
						onSave={handleSaveColumns}
						docType="Purchase Receipt"
						defaultSelected={defaultSelectedColumns}
						fieldsData={columnsConfig.map((col) => ({
							fieldname: col.accessor,
							label: col.name,
						}))}
						disabledFields={[
							"siNo",
							"name",
							"posting_date",
							"status",
							"action",
						]}
					/>
				)}

				<Table
					columns={visibleColumns}
					data={grnData.purchase_receipt}
					loading={loading}
				/>

				<Pagination
					setCurrentPage={setCurrentPage}
					pageSize={pageSize}
					totalRecords={grnData.total_no_of_records}
					currentPage={currentPage}
					className="mt-2 pb-3"
				/>
				{delPopupVisible && (
					<DelConfirmPopup
						alredysubmitdata={alreadySubmitData}
						delSelectedId={delSelectedId}
						visible={delPopupVisible}
						message={delmessage}
						onConfirm={delHandleConfirm}
						onCancel={delHandleCancel}
						delDone={delDone}
					/>
				)}
				{showExportPopup && (
					<ExportCSV
						doctype="Purchase Receipt"
						fieldsData={accessors}
						onClose={() => setShowExportPopup(false)}
					/>
				)}
			</ListLayout>
		</div>
	);
};

export default MainContent;